import { useEffect, useRef, useState } from 'react';
import type { StreamEvent } from '@hypevault/shared-types';

/**
 * Marathon (Compact) — docked timer + slide-out event reveal.
 *
 * Idle:        ┌─[ 02:31:45 ]─┐         (gradient pill only)
 * Reveal L:    ┌─[ 02:31:45 ]│ +30s ───────┐
 * Reveal R:    ┌──────── +30s │[ 02:31:45 ]─┐
 *
 * The shell is a single rounded container with overflow:hidden; the
 * reveal panel's width animates 0 → target so it appears to slide out
 * from behind the timer toward the screen interior. Dock side = left
 * means the OBS browser source sits flush with the left edge of the
 * stream; reveal expands rightward. Dock=right reverses it.
 *
 * Data flow:
 *   - Polls /api/marathon/public?token=<jwt> every 2s for ground truth.
 *   - Refetches immediately on any StreamEvent so the reveal fires within
 *     a frame of a real contribution landing in the DB.
 *   - Diffs recent[] against a lastSeen ref so old contributions don't
 *     replay every poll, and so first load doesn't blast through the
 *     8-entry backlog.
 *   - Ticks remaining = endsAt - now every 500ms locally between polls.
 *
 * Config (all optional):
 *   dock: 'left' | 'right'                (default 'left')
 *   gradient: 'sunset'|'aurora'|'fire'|'ocean'|'candy'   (default 'sunset')
 *   expandedHoldSeconds: number            (default 3.5)
 *   fontSize: number                       (default 36)
 *   showEventLabel: boolean                (default true)
 */

interface MarathonSnapshot {
  isActive: boolean;
  name: string | null;
  endsAt: string | null;
  pausedAt: string | null;
  startedAt: string | null;
  initialDurationSec: number | null;
  recent: Array<{
    contributorName: string | null;
    eventType: string;
    secondsAdded: number;
    createdAt: string;
  }>;
  powerHour: { multiplier: number; endsAt: string | null } | null;
}

interface RevealItem {
  key: string;
  contributorName: string;
  eventType: string;
  secondsAdded: number;
}

const GRADIENTS: Record<string, string> = {
  sunset: 'linear-gradient(135deg, #f655a6 0%, #ff8a4c 50%, #ffcc33 100%)',
  aurora: 'linear-gradient(135deg, #22d3ee 0%, #a855f7 50%, #f655a6 100%)',
  fire: 'linear-gradient(135deg, #ef4444 0%, #f97316 50%, #facc15 100%)',
  ocean: 'linear-gradient(135deg, #14b8a6 0%, #3b82f6 50%, #8b5cf6 100%)',
  candy: 'linear-gradient(135deg, #f655a6 0%, #c084fc 50%, #60a5fa 100%)',
};

const GRADIENT_ACCENT: Record<string, string> = {
  sunset: '#ff8a4c',
  aurora: '#a855f7',
  fire: '#f97316',
  ocean: '#3b82f6',
  candy: '#c084fc',
};

const EVENT_LABEL: Record<string, string> = {
  follow: 'FOLLOW',
  kick_follow: 'KICK FOLLOW',
  sub_t1: 'SUB',
  sub_t2: 'TIER 2 SUB',
  sub_t3: 'TIER 3 SUB',
  sub_prime: 'PRIME SUB',
  yt_member: 'MEMBER',
  kick_sub: 'KICK SUB',
  kick_gift_sub: 'GIFT KICK SUB',
  cheer: 'CHEER',
  kick_kicks: 'KICKS',
  raid: 'RAID',
  yt_superchat: 'SUPERCHAT',
  yt_supersticker: 'STICKER',
  tip: 'TIP',
  hypevault_card_redemption: 'CARD',
  hypevault_card_purchase: 'PURCHASE',
  manual_adjust: 'HOST',
};

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatAdded(seconds: number): string {
  if (seconds < 60) return `+${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `+${m}m` : `+${m}m ${s}s`;
}

interface TestReveal {
  id: string;
  eventType: string;
  contributorName: string;
  secondsAdded: number;
  createdAt: string;
}

export function MarathonCompactWidget({
  fields,
  token,
  latestEvent,
  testReveal,
}: {
  fields: Record<string, unknown>;
  token: string;
  latestEvent: StreamEvent | null;
  testReveal?: TestReveal | null;
}) {
  const dock = ((fields.dock as 'left' | 'right' | undefined) ?? 'left') as 'left' | 'right';
  const gradientKey = String(fields.gradient ?? 'sunset');
  const gradient = GRADIENTS[gradientKey] ?? GRADIENTS.sunset!;
  const accent = GRADIENT_ACCENT[gradientKey] ?? GRADIENT_ACCENT.sunset!;
  const holdMs = Math.max(1500, Number(fields.expandedHoldSeconds ?? 3.5) * 1000);
  const fontSize = Number(fields.fontSize ?? 36);
  const showEventLabel = (fields.showEventLabel as boolean | undefined) ?? true;

  const [snapshot, setSnapshot] = useState<MarathonSnapshot | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [powerHourRemaining, setPowerHourRemaining] = useState(0);
  const [reveal, setReveal] = useState<RevealItem | null>(null);
  const [phase, setPhase] = useState<'idle' | 'expanding' | 'showing' | 'collapsing'>('idle');
  // Queue lives in state so each push triggers a re-render, which re-runs the
  // drain effect below. A ref-backed queue silently dropped reveals because
  // nothing kicked the drain effect after the push.
  const [queue, setQueue] = useState<RevealItem[]>([]);

  const lastSeenCreatedAtRef = useRef<string | null>(null);
  const lastEventIdRef = useRef<string | null>(null);
  const lastTestRevealIdRef = useRef<string | null>(null);

  // ───── Snapshot fetch + diff ─────
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = () => {
      fetch(`/api/marathon/public?token=${encodeURIComponent(token)}`, { cache: 'no-store' })
        .then((r) => (r.ok ? (r.json() as Promise<MarathonSnapshot>) : null))
        .then((data) => {
          if (cancelled || !data) return;
          setSnapshot(data);
          if (!data.recent || data.recent.length === 0) return;
          // recent[] is DESC by createdAt — newest first.
          const newest = data.recent[0]!.createdAt;
          if (lastSeenCreatedAtRef.current === null) {
            // First poll: establish baseline, do NOT replay history.
            lastSeenCreatedAtRef.current = newest;
            return;
          }
          const lastSeen = lastSeenCreatedAtRef.current;
          const fresh = data.recent
            .filter((r) => r.createdAt > lastSeen && r.secondsAdded > 0)
            .sort((a, b) => a.createdAt.localeCompare(b.createdAt)); // oldest first → chronological play
          if (fresh.length === 0) return;
          const items: RevealItem[] = fresh.map((r) => ({
            key: r.createdAt,
            contributorName: r.contributorName ?? 'Someone',
            eventType: r.eventType,
            secondsAdded: r.secondsAdded,
          }));
          setQueue((q) => [...q, ...items]);
          lastSeenCreatedAtRef.current = newest;
        })
        .catch(() => undefined);
    };
    load();
    const interval = setInterval(load, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [token]);

  // ───── Refetch immediately on every StreamEvent so the reveal lands fast ─────
  useEffect(() => {
    if (!latestEvent || !token) return;
    if (latestEvent.id === lastEventIdRef.current) return;
    lastEventIdRef.current = latestEvent.id;
    // Tiny delay so applyEventToMarathon's DB write has landed by the time
    // we read recent[]. publishEvent fires after the write but there's
    // still some commit propagation latency on busy nights.
    const t = setTimeout(() => {
      fetch(`/api/marathon/public?token=${encodeURIComponent(token)}`, { cache: 'no-store' })
        .then((r) => (r.ok ? (r.json() as Promise<MarathonSnapshot>) : null))
        .then((data) => {
          if (!data) return;
          setSnapshot(data);
          if (!data.recent || data.recent.length === 0) return;
          const lastSeen = lastSeenCreatedAtRef.current;
          if (lastSeen === null) {
            lastSeenCreatedAtRef.current = data.recent[0]!.createdAt;
            return;
          }
          const fresh = data.recent
            .filter((r) => r.createdAt > lastSeen && r.secondsAdded > 0)
            .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
          if (fresh.length === 0) return;
          const items: RevealItem[] = fresh.map((r) => ({
            key: r.createdAt,
            contributorName: r.contributorName ?? 'Someone',
            eventType: r.eventType,
            secondsAdded: r.secondsAdded,
          }));
          setQueue((q) => [...q, ...items]);
          lastSeenCreatedAtRef.current = data.recent[0]!.createdAt;
        })
        .catch(() => undefined);
    }, 300);
    return () => clearTimeout(t);
  }, [latestEvent, token]);

  // ───── Test reveals (synthetic — bypasses DB diff, fires immediately) ─────
  useEffect(() => {
    if (!testReveal) return;
    if (testReveal.id === lastTestRevealIdRef.current) return;
    lastTestRevealIdRef.current = testReveal.id;
    setQueue((q) => [
      ...q,
      {
        key: testReveal.id,
        contributorName: testReveal.contributorName,
        eventType: testReveal.eventType,
        secondsAdded: testReveal.secondsAdded,
      },
    ]);
  }, [testReveal]);

  // ───── Local tick so the digit counts down smoothly between polls ─────
  useEffect(() => {
    if (!snapshot) return;
    const tick = () => {
      const ref = snapshot.pausedAt ? new Date(snapshot.pausedAt).getTime() : Date.now();
      if (snapshot.endsAt) {
        const endsAt = new Date(snapshot.endsAt).getTime();
        setRemaining(Math.max(0, Math.floor((endsAt - ref) / 1000)));
      } else {
        setRemaining(0);
      }
      // Power-hour countdown shares the same paused-ref so paused
      // marathons freeze both digits, not just the main timer.
      if (snapshot.powerHour?.endsAt) {
        const phEnds = new Date(snapshot.powerHour.endsAt).getTime();
        setPowerHourRemaining(Math.max(0, Math.floor((phEnds - ref) / 1000)));
      } else {
        setPowerHourRemaining(0);
      }
    };
    tick();
    const interval = setInterval(tick, 500);
    return () => clearInterval(interval);
  }, [snapshot]);

  // ───── Drain reveal queue while idle (just hops state — no timers) ─────
  // Kept separate from the timer effect below because the setQueue/setPhase
  // calls in here mutate deps that would otherwise re-run this effect and
  // trip its cleanup, cancelling the very timeouts that drive the animation.
  useEffect(() => {
    if (phase !== 'idle') return;
    if (queue.length === 0) return;
    const next = queue[0]!;
    setQueue((q) => q.slice(1));
    setReveal(next);
    setPhase('expanding');
  }, [phase, queue]);

  // ───── Phase progression timers — keyed only on `reveal` ─────
  // Re-runs once when a new reveal is set and once again when it clears.
  // Cleanup only fires on those transitions, so the timers it schedules
  // get to live their full lifetime.
  useEffect(() => {
    if (!reveal) return;
    const EXPAND_MS = 350;
    const COLLAPSE_MS = 350;
    const t1 = setTimeout(() => setPhase('showing'), EXPAND_MS);
    const t2 = setTimeout(() => setPhase('collapsing'), EXPAND_MS + holdMs);
    const t3 = setTimeout(
      () => {
        setPhase('idle');
        setReveal(null);
      },
      EXPAND_MS + holdMs + COLLAPSE_MS,
    );
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [reveal, holdMs]);

  // Render whenever there's an active marathon OR a test/queued reveal is in
  // flight — lets the streamer preview the expand animation from the widget
  // settings page even before a marathon is started.
  const expanded = phase !== 'idle' && reveal !== null;
  const hasActiveMarathon = snapshot?.isActive === true;
  if (!hasActiveMarathon && !expanded && queue.length === 0) return null;
  const isPaused = snapshot?.pausedAt != null;

  // Reveal panel target width — wide enough for "+1m 30s" big text + name/event line.
  const revealTargetWidth = 280;

  // Power hour state — chip floats above the timer pill, edge-flush with the
  // dock side so it never disturbs the slide-out reveal layout.
  const powerHourActive = !!snapshot?.powerHour && powerHourRemaining > 0;
  const powerHourMultiplier = snapshot?.powerHour?.multiplier ?? 0;

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: dock === 'left' ? 'flex-start' : 'flex-end',
        alignItems: 'center',
        padding: 8,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <div
        style={{
          position: 'relative',
          // Outer wrapper exists ONLY to host the absolutely-positioned
          // power-hour chip below the rounded shell — keeping the shell's
          // overflow:hidden intact so the slide-out reveal still clips
          // cleanly.
          display: 'inline-flex',
          maxHeight: '100%',
        }}
      >
        {powerHourActive && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              [dock === 'left' ? 'left' : 'right']: 14,
              padding: '4px 10px 4px 8px',
              borderRadius: 999,
              background: gradient,
              color: '#fff',
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: `0 6px 18px -4px rgba(0,0,0,0.55), 0 0 0 1px ${accent}66, 0 0 20px -4px ${accent}cc`,
              fontFamily: "'Inter', system-ui, sans-serif",
              zIndex: 2,
              animation: 'mc-power-pulse 1.8s ease-in-out infinite',
              textShadow: '0 1px 2px rgba(0,0,0,0.4)',
              transformOrigin: dock === 'left' ? 'left center' : 'right center',
            }}
          >
            <span aria-hidden style={{ fontSize: 13, lineHeight: 1 }}>
              ⚡
            </span>
            <span>Power Hour ×{powerHourMultiplier}</span>
            <span
              style={{
                fontVariantNumeric: 'tabular-nums',
                fontWeight: 800,
                fontSize: 10,
                paddingLeft: 6,
                marginLeft: 2,
                borderLeft: '1px solid rgba(255,255,255,0.35)',
                color: 'rgba(255,255,255,0.95)',
              }}
            >
              {formatTime(powerHourRemaining)}
            </span>
          </div>
        )}
        <div
          style={{
            display: 'inline-flex',
            flexDirection: dock === 'left' ? 'row' : 'row-reverse',
            alignItems: 'stretch',
            borderRadius: 14,
            overflow: 'hidden',
            boxShadow: powerHourActive
              ? `0 12px 36px -12px rgba(0,0,0,0.6), 0 0 0 1px ${accent}66, 0 0 28px -2px ${accent}88`
              : '0 12px 36px -12px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)',
            maxHeight: '100%',
            transition: 'box-shadow 350ms ease',
          }}
        >
          {/* ───── Timer pill (gradient) ───── */}
          <div
            style={{
              background: gradient,
              padding: '0 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              position: 'relative',
              color: '#fff',
              fontWeight: 900,
              fontSize,
              letterSpacing: '-0.02em',
              textShadow: '0 1px 2px rgba(0,0,0,0.35), 0 0 18px rgba(255,255,255,0.18)',
              fontVariantNumeric: 'tabular-nums',
              opacity: isPaused ? 0.75 : 1,
              transition: 'opacity 250ms ease',
            }}
          >
            {/* Subtle top-edge sheen for that glossy look */}
            <span
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '50%',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.18), transparent)',
                pointerEvents: 'none',
              }}
            />
            {isPaused && (
              <span
                aria-hidden
                style={{
                  display: 'inline-block',
                  fontSize: fontSize * 0.55,
                  lineHeight: 1,
                  fontWeight: 900,
                  color: 'rgba(255,255,255,0.85)',
                }}
              >
                ‖
              </span>
            )}
            <span style={{ position: 'relative' }}>{formatTime(remaining)}</span>
          </div>

          {/* ───── Reveal panel (dark) ───── */}
          <div
            style={{
              width: expanded ? revealTargetWidth : 0,
              transition: 'width 350ms cubic-bezier(0.16, 1, 0.3, 1)',
              overflow: 'hidden',
              willChange: 'width',
              background: 'linear-gradient(180deg, #0f0c1d 0%, #07050f 100%)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: dock === 'left' ? 'flex-start' : 'flex-end',
              padding: expanded ? '8px 18px' : '8px 0',
              color: '#fff',
              // Inset edge glow on the side that meets the timer.
              boxShadow:
                dock === 'left' ? `inset 2px 0 0 ${accent}55` : `inset -2px 0 0 ${accent}55`,
            }}
          >
            {reveal && (
              <>
                <div
                  style={{
                    fontSize: Math.max(20, fontSize * 0.85),
                    fontWeight: 900,
                    lineHeight: 1,
                    color: accent,
                    textShadow: `0 0 16px ${accent}66`,
                    letterSpacing: '-0.02em',
                    opacity: phase === 'showing' ? 1 : 0,
                    transform: phase === 'showing' ? 'translateX(0)' : translateInDir(dock, 8),
                    transition: 'opacity 220ms ease, transform 280ms cubic-bezier(0.16, 1, 0.3, 1)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatAdded(reveal.secondsAdded)}
                </div>
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 1.2,
                    color: 'rgba(255,255,255,0.7)',
                    lineHeight: 1.2,
                    opacity: phase === 'showing' ? 1 : 0,
                    transform: phase === 'showing' ? 'translateX(0)' : translateInDir(dock, 12),
                    transition:
                      'opacity 220ms 80ms ease, transform 280ms 80ms cubic-bezier(0.16, 1, 0.3, 1)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: revealTargetWidth - 36,
                    textAlign: dock === 'left' ? 'left' : 'right',
                    width: '100%',
                  }}
                >
                  <span style={{ color: '#fff' }}>{reveal.contributorName}</span>
                  {showEventLabel && (
                    <>
                      <span style={{ opacity: 0.4 }}> · </span>
                      <span>{EVENT_LABEL[reveal.eventType] ?? reveal.eventType.toUpperCase()}</span>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes mc-power-pulse {
          0%, 100% { transform: scale(1); filter: brightness(1); }
          50% { transform: scale(1.04); filter: brightness(1.15); }
        }
      `}</style>
    </div>
  );
}

// Reveal contents slide from the side the timer is on, toward the visible
// edge — feels like the panel pushed them out into view.
function translateInDir(dock: 'left' | 'right', px: number): string {
  return dock === 'left' ? `translateX(-${px}px)` : `translateX(${px}px)`;
}
